import { zodResolver } from '@hookform/resolvers/zod';
import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorText } from '@/shared/api';
import { z } from 'zod';

import { Button, TextField } from '@/shared/ui';

import { useStaffLogin } from '../api/useStaffLogin';

interface StaffLoginFormProps {
  readonly onSignedIn: () => void;
}

/**
 * The client validates **shape** only; whether the credentials are real is the
 * server's business and it answers with one generic code either way
 * (CLAUDE.md §5.6, §3.8). Messages are translation keys, resolved at render.
 */
const credentialsSchema = z.object({
  email: z.string().min(1, 'required').email('email'),
  password: z.string().min(1, 'required'),
});

type CredentialsForm = z.infer<typeof credentialsSchema>;

export function StaffLoginForm({ onSignedIn }: StaffLoginFormProps): JSX.Element {
  const { t } = useTranslation(['auth', 'validation', 'errors', 'common']);
  const errorText = useErrorText();
  const login = useStaffLogin();

  const form = useForm<CredentialsForm>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });

  const submit = form.handleSubmit((values): void => {
    login.mutate(values, { onSuccess: onSignedIn });
  });

  const message = (key: string | undefined): string | undefined =>
    key === undefined ? undefined : t(key, { ns: 'validation' });

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event): void => {
        void submit(event);
      }}
    >
      <TextField
        autoComplete="email"
        error={message(form.formState.errors.email?.message)}
        inputMode="email"
        label={t('auth:staff.emailLabel')}
        type="email"
        {...form.register('email')}
      />

      <TextField
        autoComplete="current-password"
        error={message(form.formState.errors.password?.message)}
        label={t('auth:staff.passwordLabel')}
        type="password"
        {...form.register('password')}
      />

      {login.error === null ? null : (
        <p className="type-caption text-danger" role="alert">
          {errorText(login.error)}
        </p>
      )}

      <Button fullWidth loading={login.isPending} size="lg" type="submit" variant="primary">
        {t('auth:staff.signIn')}
      </Button>
    </form>
  );
}
